<script setup lang="ts">
import type { Campaign } from '#shared/utils/campaign'

definePageMeta({
  name: 'products',
  middleware: ['migrate-product-formats'],
})

const version = useCrystallizeVersion()

const { time } = useServerTiming()

const { data: campaign } = useQuery<Campaign | null>({
  queryKey: ['campaign', 'aunivers', version],
  staleTime: 1000 * 60 * 60,
  queryFn: async () => {
    const data = await time('GetAuniversCampaign', () =>
      $crystallizeFetch({
        document: 'page',
        path: '/kampanjer/aunivers',
        version,
      }))

    return toCampaign(data.catalogue)
  },
})

const { data: productCatalogue, suspense } = useProducts()

await suspense()

const route = useRoute()

const { filteredGroups, filtered, filters, levels } = useProductFilters(() => productCatalogue.value?.products)

const router = useRouter()

const activeTab = computed({
  get() {
    return LEVELS.find((l) => l === route.query.level) ?? ALL
  },
  set(tab) {
    router.push({
      query: {
        level: tab === route.query.level ? undefined : LEVELS.find((l) => l === tab),
      },
    })
  },
})

useHead({
  bodyAttrs: {
    class: 'bg-green-50',
  },
})
</script>

<template>
  <div class="[--grid-gap:calc(var(--spacing)*5.5)]">
    <LayoutBreadCrumbs :background="true" />
    <UContainer class="mb-m flex flex-col items-center justify-center gap-m self-center lg:mb-xl lg:gap-l" as="header">
      <div class="flex max-w-2xl flex-col gap-s self-center pt-legacy-l text-center lg:gap-l">
        <h1
          id="page-title"
          class="font-headline text-3xl font-medium text-green-pop lg:text-6xl"
          v-text="productCatalogue?.title ?? 'Finn læremidler'"
        />
        <div
          v-if="productCatalogue?.description"
          class="text-sm text-balance text-green-5 lg:text-xl"
          v-html="productCatalogue.description"
        />
      </div>
      <UiCampaignCallout class="mx-auto" :campaign="campaign" />

      <USelect
        v-model="activeTab"
        :items="levels"
        :multiple="false"
        class="lg:hidden"
        :ui="{
          base: 'bg-green-40 !p-s ring-0 hover:bg-green-30 text-green-5 rounded-sm max-w-lg w-full',
        }"
      />

      <ProductCatalogFilter
        class="flex w-full max-w-lg flex-col rounded-tr-4xl rounded-bl-4xl bg-green-5 px-m pt-xs pb-s text-green-30 lg:hidden"
        :filters="filters"
      />
    </UContainer>

    <!-- Mobile view -->
    <UContainer
      id="main-content"
      as="main"
      aria-labelledby="page-title"
      class="bg-white py-5 lg:hidden"
    >
      <UiProductsCatalog :products="filtered" class="gap-y-(--grid-gap)" />
    </UContainer>

    <!-- Desktop view -->
    <UTabs
      v-model="activeTab"
      :items="levels"
      class="hidden lg:block"
      :ui="{
        root: 'gap-0',
        // FIXME: padding in x dir should be dynamic
        list: 'w-full max-w-(--ui-container) mx-auto bg-inherit py-0 px-(--spacing-legacy-m) items-end [--roundness:23px] min-h-20',
        content: 'bg-white w-full pt-5 pb-24',
        trigger: 'bg-green-40 hover:bg-green-30 !text-green-5 data-active:!text-green-50 rounded-b-none rounded-t-(--roundness) p-4 text-xl data-active:bg-white transition-all font-normal data-active:font-semibold data-active:py-6 h-fit',
        indicator: 'bg-white rounded-b-none rounded-t-(--roundness) inset-y-0 shadow-none',
        label: 'text-inherit',
      }"
    >
      <template #leading="{ item }">
        <UIcon
          v-if="item.value !== ALL && item.value === activeTab"
          name="fa7-solid:close"
          class="size-3"
        />
      </template>
      <template #content>
        <div class="relative grid grid-cols-[1fr_auto] xl:grid-cols-[1fr_auto_1fr]">
          <ProductCatalogFilter
            is="aside"
            class="sticky-against-header z-1 mx-0 ml-legacy-s flex w-fit flex-col self-start justify-self-end rounded-bl-[53px] bg-green-5 p-m pt-xs [--gap:var(--spacing-s)] md:ml-legacy-m"
            :filters="filters"
            default-open
            multiple
          />

          <UContainer
            id="main-content"
            as="main"
            aria-labelledby="page-title"
            class="flex flex-col gap-y-(--grid-gap)"
          >
            <UiProductsCatalog
              v-for="(group, i) in filteredGroups"
              :key="i"
              :products="group"
              class="gap-y-(--grid-gap)"
            />
          </UContainer>
        </div>
      </template>
    </UTabs>
  </div>
</template>
